<template>
  <div :class="{ 'with-floating-bar': withFloatingBar }" class="page-wrapper">
    <breadcrumbs v-if="breadcrumbs" :items="breadcrumbs" class="page-wrapper__breadcrumbs" />

    <div class="page-wrapper__title" :class="{ sticky: stickyTitle, smallGap: slots.secondRow && transparentBg }">
      <slot name="title" />
    </div>

    <div v-if="slots.subtitle" class="page-wrapper__subtitle">
      <slot name="subtitle" />
    </div>

    <component :is="errorKey ? ErrorBoundary : 'div'" :error-key="errorKey" v-bind="contentProps">
      <div v-if="tabs" class="page-wrapper__tabs">
        <div class="page-wrapper__tabs-container">
          <div
            v-if="$vuetify.display.xs && activeTab"
            :class="{ active: isShowTabsNav }"
            class="page-wrapper__tab paragraph-s page-wrapper__active-tab"
            v-click-outside="hideTabsNav"
            @click="toggleTabsNav"
          >
            <e-icon v-if="activeTab.icon" :name="activeTab.icon" :size="20" />
            <b class="ml-2">{{ activeTab.title }}</b>
            <e-icon v-if="activeTab.icon" name="arrowDropdown" :size="10" class="ml-2 page-wrapper__active-tab-arrow" />
          </div>
          <div class="page-wrapper__tabs-nav" :class="{ shown: isShowTabsNav }">
            <router-link
              v-for="(tab, index) in tabs"
              :key="index"
              :to="tab.to"
              :class="{ disabled: tab.disabled }"
              class="page-wrapper__tab paragraph-s"
            >
              <e-icon v-if="tab.icon" :name="tab.icon" :size="20" />
              <b class="ml-3">{{ tab.title }}</b>
            </router-link>
          </div>
        </div>
      </div>

      <page-block
        full-page
        :transparent-bg="transparentBg"
        :class="{ 'with-padding': slots.secondRow && !transparentBg }"
      >
        <div
          v-if="slots.secondRow"
          class="page-wrapper__second-row"
          :class="{ sticky: stickySecondRow, 'body-bg': transparentBg }"
        >
          <slot name="secondRow" />
        </div>

        <slot />
      </page-block>
    </component>
  </div>
</template>

<script setup lang="ts">
import { ref, toRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { PageWrapperTab } from '@/core/types/components/components';
import type { BreadcrumbsItem } from '@/core/types/components/helps';
import type { PageErrorKey } from '@/parts/errors/store/pageErrorStore';
import Breadcrumbs from '@/core/components/interface/Breadcrumbs.vue';
import PageBlock from '@/core/components/interface/PageBlock.vue';
import { EIcon } from '@/core/components/ui';
import ErrorBoundary from '@/parts/errors/components/ErrorBoundary.vue';

const props = defineProps<{
  transparentBg?: boolean;
  stickyTitle?: boolean;
  stickySecondRow?: boolean;
  breadcrumbs?: BreadcrumbsItem[];
  withFloatingBar?: boolean;
  errorKey?: PageErrorKey;
  tabs?: PageWrapperTab[];
}>();

const slots = defineSlots<{
  title?: (props: unknown) => void;
  secondRow?: (props: unknown) => void;
  default?: (props: unknown) => void;
  subtitle?: (props: unknown) => void;
}>();

const $route = useRoute();
const $router = useRouter();

const contentProps = toRef(() => {
  const obj = {} as {
    class?: 'contents';
  };

  if (!props.errorKey) {
    obj.class = 'contents';
  }

  return obj;
});

const activeTab = toRef(() => props.tabs?.find((tab) => $router.resolve(tab.to).name === $route.name));

const isShowTabsNav = ref(false);

const toggleTabsNav = () => {
  isShowTabsNav.value = !isShowTabsNav.value;
};
const hideTabsNav = () => {
  isShowTabsNav.value = false;
};
</script>

<style scoped lang="scss">
.page-wrapper {
  display: flex;
  flex-direction: column;

  height: 100%;

  .contents {
    display: contents;
  }

  &.with-floating-bar {
    @include sm-and-down {
      padding-bottom: 60px;
    }
  }

  .with-padding {
    padding-top: 8px;

    @include md-and-down {
      padding-top: 14px;
    }
  }

  &__breadcrumbs {
    margin-top: $gap-3;
    margin-bottom: $gap-3;

    @include md-and-down {
      padding: 0 24px;
    }

    @include xs {
      padding: 0 16px;
    }
  }

  &__title {
    display: flex;
    align-items: center;

    box-sizing: content-box;

    min-height: 24px;
    margin-bottom: 16px;
    padding: 8px 0;

    @include md-and-down {
      margin-bottom: 12px;
      padding: 8px 24px;
    }

    @include xs {
      padding: $gap-3 $gap-4;
    }

    &.smallGap {
      margin-bottom: 0;

      @include md-and-down {
        padding-bottom: 0;
      }

      @include xs {
        padding-bottom: 4px;
      }
    }

    &.sticky {
      position: sticky;
      top: 0;
      z-index: 20;

      background-color: $body-bg-color;
    }
  }

  &__subtitle {
    margin: 0 0 16px;

    @include md-and-down {
      padding: 0 24px;
    }

    @include xs {
      padding: 0 16px;
    }
  }

  &__second-row {
    margin-bottom: $gap-3;
    padding: $gap-4 0 $gap-3;

    @include md-and-down {
      padding-top: 12px;
    }

    @include xs {
      margin-bottom: 10px;
      padding: $gap-3 0 $gap-2;
    }

    &.sticky {
      position: sticky;
      top: 0;
      z-index: 999;

      background-color: $main-bg-color;

      &.body-bg {
        background-color: $body-bg-color;
      }
    }
  }

  &__tabs {
    position: relative;

    background-color: $primary-tonal-color;
    border-radius: $border-radius-l $border-radius-l 0 0;

    &-container {
      position: relative;

      display: flex;

      width: fit-content;
    }

    &:after {
      content: '';
      position: absolute;
      top: 100%;
      right: 0;
      left: 0;
      z-index: -1;

      height: 20px;

      background-color: $primary-tonal-color;
    }

    &-nav {
      display: contents;

      @include xs {
        position: absolute;
        top: 100%;
        right: 0;
        left: 0;
        z-index: $z-index-4;

        display: none;

        background-color: $main-bg-color;
        border-radius: 0 0 $border-radius-l $border-radius-l;
        box-shadow: $box-shadow;

        .router-link-exact-active {
          display: none;
        }

        &.shown {
          display: block;
        }
      }
    }
  }

  &__tab {
    display: flex;
    align-items: center;

    padding: $gap-4 $gap-5;

    border-radius: $border-radius-l $border-radius-l 0 0;

    @include hover {
      color: $primary-color;
    }

    @include md-and-down {
      b {
        display: none;
      }
    }

    @include xs {
      padding: 12px $gap-5;

      b {
        display: block;
      }
    }

    &.disabled {
      color: $text-inactive-color;

      pointer-events: none;
    }

    &.page-wrapper__active-tab {
      z-index: $z-index-5;

      .page-wrapper__active-tab-arrow {
        transition: transform $transition-duration;
      }

      &.active {
        &:after {
          display: none;
        }

        .page-wrapper__active-tab-arrow {
          transform: rotate(180deg);
        }
      }
    }

    &.router-link-exact-active,
    &.page-wrapper__active-tab {
      position: relative;

      color: $primary-color;

      background-color: $main-bg-color;

      @include md-and-down {
        b {
          display: inline;
        }
      }

      &:after {
        content: '';
        position: absolute;
        top: 100%;
        right: 0;
        left: 0;

        height: 20px;

        background-color: $main-bg-color;
      }
    }
  }
}
</style>
